/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from "react";
import { View, Text, StyleSheet, Dimensions, Platform, ScrollView } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LhButton from "../../../../components/LhButton";
import LHOverlay from "../../../../components/LHOverlay";
import EmployeeFormSection from "./EmployeeFormSection";
import { useDispatch } from "react-redux";
import { fetchEmployees } from "../../../../store/slices/employeeUpdatesSlice";
import FontFamily from "../../../../constants/fonts";
import { DLOButton } from "../../../../modules/dlo/components";
import { useTheme } from "../../../../theme/useTheme";

interface EmployeeUpdatesHeaderProps {
  onBackPress?: () => void;
  statusOptions: Array<{ id: string; displayText: string }>;
  managerOptions: Array<{ id: string; displayText: string }>;
  selectedLocation?: {
    id: string; displayText: string 
};
  onOverlayVisibilityChange?: (visible: boolean) => void; // Callback to notify parent of overlay state
}

const isMobileView = () => {
  const windowWidth = Dimensions.get("window").width;
  return (
    Platform.OS === "ios" || Platform.OS === "android" || windowWidth < 768
  );
};

// Determine if the current view is iPhone specifically
const isIPhoneView = () => {
  const windowWidth = Dimensions.get("window").width;
  return Platform.OS === "ios" && windowWidth < 768;
};

// Helper function to check if device should use web-style layout (web or iPad)
const isWebOrIPad = () => {
  const windowWidth = Dimensions.get("window").width;
  return Platform.OS === "web" || (Platform.OS === "ios" && windowWidth >= 768);
};

const EmployeeUpdatesHeader: React.FC<EmployeeUpdatesHeaderProps> = ({
  onBackPress,
  statusOptions,
  managerOptions,
  selectedLocation,
  onOverlayVisibilityChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark" && Platform.OS !== "web";

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [hireDate, setHireDate] = useState<Date | null>(null);
  const [graduationDate, setGraduationDate] = useState<Date | null>(null);
  
  // Create ref to access the form reset function
  const formResetRef = useRef<(() => void) | null>(null);

  // Check if device is strictly mobile phone (iPhone only, not iPad)
  const isMobilePhone = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768;
  };

  const handleShowOverlay = () => {
    // Reset dates when showing overlay
    setHireDate(null);
    setGraduationDate(null);
    
    // Reset form values if the reset function is available
    if (formResetRef.current) {
      formResetRef.current();
    }
    
    setIsOverlayVisible(true);
    // Notify parent of overlay state change
    if (onOverlayVisibilityChange) {
      onOverlayVisibilityChange(true);
    }
  };

  const handleDismissOverlay = () => {
    setIsOverlayVisible(false);
    // Reset dates when dismissing overlay
    setHireDate(null);
    setGraduationDate(null);
    
    // Reset form values if the reset function is available
    if (formResetRef.current) {
      formResetRef.current();
    }
    
    // Notify parent of overlay state change
    if (onOverlayVisibilityChange) {
      onOverlayVisibilityChange(false);
    }
  };

  // Get dispatch for Redux actions
  const dispatch = useDispatch<any>();

  const handleAddEmployee = () => {
    // This will be called after successful employee addition
    // Close overlay immediately
    handleDismissOverlay();

    // Refresh the employee list if location is selected
    if (selectedLocation?.displayText) {
      // Fetch employees from the API to refresh the list
      dispatch(fetchEmployees({ location: selectedLocation.displayText }));
    }
  };

  const isMobile = isMobileView();
  const isIPhone = isIPhoneView();

  // Helper function to render the form content
  const renderFormContent = () => {
    const content = (
      <View style={[
        isMobilePhone() ? styles.mobileFullScreenContent : styles.overlayContent,
        isDark && isMobilePhone() ? { backgroundColor: colors.background.page } : undefined,
      ]}>
        {isMobilePhone() && (
          <View style={styles.mobileHeader}>
            <LhButton
              text="← Back"
              variant="secondary"
              size="small"
              onPress={handleDismissOverlay}
              style={styles.mobileBackButton}
              textStyle={[styles.mobileBackButtonText, isDark ? { color: colors.brand.primary } : undefined]}
            />
            <Text style={[styles.mobileTitle, isDark ? { color: colors.text.primary } : undefined]}>Add New Employee</Text>
          </View>
        )}
        <View style={styles.row}>
          <View style={styles.dateField}>
            <EmployeeFormSection
              statusOptions={statusOptions}
              managerOptions={managerOptions}
              onAddEmployee={handleAddEmployee}
              onCancel={handleDismissOverlay}
              hireDate={hireDate}
              setHireDate={setHireDate}
              graduationDate={graduationDate}
              setGraduationDate={setGraduationDate}
              onResetForm={formResetRef}
            />
          </View>
        </View>
      </View>
    );

    // Wrap in ScrollView for iPhone to enable scrolling
    if (isMobilePhone()) {
      return (
        <ScrollView
          style={[styles.mobileScrollView, isDark ? { backgroundColor: colors.background.page } : undefined]}
          contentContainerStyle={styles.mobileScrollViewContent}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
        >
          {content}
        </ScrollView>
      );
    }

    return content;
  };

  return (
    <>
      {/* Hide header content on iPhone when overlay is visible */}
      {!(isMobilePhone() && isOverlayVisible) && (
        <View style={styles.welcomeCard}>
          <View style={[
            styles.welcomeContainer,
            isIPhone && styles.welcomeContainerIPhone
          ]}>
            <View style={styles.welcomeLeftContent}>
              <Text
                style={[
                  styles.welcomeText,
                  isDark ? { color: colors.text.primary } : undefined,
                  Platform.OS === "web"
                    ? (({ fontFeatureSettings: "'liga' off, 'clig' off" } as any) as object)
                    : undefined,
                ]}
              >
                Employee Updates
              </Text>
            </View>
            <View
              style={isIPhone ? styles.beginButtonRowIPhone : isMobile ? styles.beginButtonRowMobile : styles.beginButtonRow}
            >
              <DLOButton
                text="+ Add Employee"
                variant="primary"
                size="medium"
                // iconSize={20}
                onPress={handleShowOverlay}
                isLoading={false}
                disabled={false}
                style={styles.beginButton}
              />
            </View>
          </View>
        </View>
      )}

      {/* Conditional rendering: Full-screen on iPhone, LHOverlay on other devices */}
      {isMobilePhone() && isOverlayVisible ? (
        // iPhone: Render content directly without LHOverlay (full-screen)
        renderFormContent()
      ) : (
        // Tablet/Web: Render content with LHOverlay
        <LHOverlay
          visible={isOverlayVisible}
          onDismiss={handleDismissOverlay}
          title="Add New Employee"
        >
          {renderFormContent()}
        </LHOverlay>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  welcomeCard: {
    
  },
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 0,
    paddingBottom: 16,
    flexWrap: isWebOrIPad() ? "nowrap" : "wrap",
  },
  welcomeContainerIPhone: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  welcomeLeftContent: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  welcomeText: {
    color: "#3A3A3A",
    fontFamily: FontFamily.bold,
    fontSize: 28,
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: 40,
    marginLeft: 0,
  },
  beginButtonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    alignItems: "center",
    width: "auto",
    marginTop: 0,
  },
  beginButtonRowMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "auto",
    marginTop: 0,
    gap: 10,
  },
  beginButtonRowIPhone: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    marginTop: 5,
  },
  beginButton: {
    width:
      Dimensions.get("window").width < 400
        ? 90
        : isWebOrIPad()
        ? "18%"
        : 120,
    minWidth: isWebOrIPad() ? 160 : 160,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    padding: Platform.OS === "web" ? 20 : 0,
  },
  overlayContent: {
    // padding: Platform.select({ web: 20, default: 16 }),
    maxWidth: isWebOrIPad() ? 1200 : "100%",
    width: "100%",
    backgroundColor: "white",
    borderRadius: 8,
    position: "relative",
    overflow: "visible",
    zIndex: 19999, // Just below the form section
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: isWebOrIPad() ? 16 : 8,
    marginBottom: 16,
    width: "100%",
  },
  dateField: {
    flex: 1,
    minWidth: isWebOrIPad() ? 200 : 150,
  },
  // Mobile full-screen styles
  mobileFullScreenContent: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 16,
    paddingTop: Platform.OS === "ios" ? 10 : 20,
  },
  mobileScrollView: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  mobileScrollViewContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  mobileHeader: {
    marginBottom: 16,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  mobileBackButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    alignSelf: "flex-start",
    marginBottom: 8,
    paddingLeft: 0,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  mobileBackButtonText: {
    color: "#005CBC",
    fontSize: 14,
    fontWeight: "400",
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#2D2D2D",
    marginBottom: 0,
    fontFamily: FontFamily.bold,
  },
});

export default EmployeeUpdatesHeader;
